<template>
  <div v-if="carDocuments" class="relative">
    <div class="mb-4 rounded-md bg-white px-4 py-1">
      <div
        v-for="(document, i) in carDocuments"
        :key="i"
        class="relative last:border-b-0"
        :class="{
          'border-b': i !== carDocuments.length - 1 || !immutable,
        }"
      >
        <div
          class="relative flex cursor-pointer items-center gap-4 pb-3 pt-3"
          @click="previewDocument(document)"
        >
          <div class="basis-1/2 md:basis-[25%]">{{ document.name }}</div>
          <div class="basis-1/2 md:basis-[20%]">
            <Badge variant="neutral" size="sm">
              {{ t(`carDocumentTypes.${document.type as string}`) }}
            </Badge>
          </div>
          <div class="hidden basis-[42%] md:block">
            {{ document.comment ? shortenText(document.comment, 70) : '-' }}
          </div>
          <div class="ml-auto" @click.stop>
            <DropdownDotted :buttons="dropdownButtons(document)" />
          </div>
        </div>
      </div>
      <div v-if="carDocuments.length === 0" class="pb-3 pt-3">
        {{ t('noDocuments') }}
      </div>
    </div>

    <div v-if="!immutable">
      <CarDocumentsForm
        v-if="addingDocument"
        :car="car"
        :car-documents="carDocuments"
        @complete="addingDocument = false"
      />
      <div v-else class="flex justify-end">
        <CVButton @click.prevent="addDocument">
          {{ t('addDocument') }}
        </CVButton>
      </div>
      <div
        v-if="isRemovingCarDocument"
        class="absolute inset-[-10px] flex items-center justify-center bg-black bg-opacity-30"
      >
        <Spinner />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CarDocumentType } from '@/entities/cars/car-document-type.enum';
import type { CarDocument } from '@/entities/cars/car-document.entity';
import type { Car } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { useCarDocumentsWithQuery, useRemoveCarDocument } from '@/queries/use-car-documents';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { Alert } from '@/services/alert.service';
import { flatMap } from 'lodash';
import { computed, ref } from 'vue';
import Badge from '@/components/Badge.vue';
import DropdownDotted from '@/components/DropdownDotted.vue';
import { shortenText } from '@/hooks/use-shorten-text';
import { downloadBlob, fetchBlobFromUrl } from '@/utils/download-file';
import CarDocumentsForm from './CarDocumentsForm.vue';

const props = defineProps<{
  car: Car;
  immutable?: boolean;
  documentTypeFilter?: CarDocumentType[];
}>();

const { t } = useI18n();
const pdfPreview = usePdfPreview();

const addingDocument = ref(false);

const { data: carDocumentsData } = useCarDocumentsWithQuery(props.car.id, {
  type: props.documentTypeFilter,
});
const { mutateAsync: removeCarDocument, isPending: isRemovingCarDocument } = useRemoveCarDocument();

const addDocument = () => {
  if (addingDocument.value) return;
  addingDocument.value = true;
};

const removeDocument = async (documentId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteDocumentTitle'),
    text: t('reallyDeleteDocumentText'),
    icon: 'question',
    showConfirmButton: false,
    showDenyButton: true,
    denyButtonText: t('delete'),
    showCancelButton: true,
    cancelButtonText: t('keep'),
  });

  if (alertResult.isDenied) {
    removeCarDocument({ carId: props.car.id, documentId });
  }
};

const downloadDocument = async (document: CarDocument) => {
  if (!document.documentFileUrl) return;
  const blob = await fetchBlobFromUrl(document.documentFileUrl);
  const extension = document.documentFileKey?.split('.').pop() ?? '';
  const fileName = extension ? `${document.name}.${extension}` : document.name;
  await downloadBlob(blob, fileName);
};

const previewDocument = async (document: CarDocument) => {
  if (!document.documentFileUrl) return;
  await pdfPreview.openPdfPreview({
    title: document.name,
    fileName: document.documentFileKey,
    load: () => fetchBlobFromUrl(document.documentFileUrl),
    errorMessage: t('couldNotLoadDocument'),
  });
};

const carDocuments = computed(() =>
  flatMap(
    carDocumentsData.value?.pages,
    (page) =>
      page.items.map((car) => ({
        ...car,
      })) ?? [],
  ),
);

const dropdownButtons = (document: CarDocument) => [
  {
    title: t('preview'),
    onClick: () => previewDocument(document),
  },
  {
    title: t('download'),
    onClick: () => downloadDocument(document),
  },
  ...(!props.immutable
    ? [
        {
          title: t('delete'),
          onClick: () => removeDocument(document.id),
        },
      ]
    : []),
  ...(document.comment
    ? [
        {
          title: t('viewComment'),
          onClick: () => {
            Alert.fire({
              text: document.comment ?? '',
              icon: 'info',
              confirmButtonText: t('close'),
            });
          },
        },
      ]
    : []),
];
</script>

<i18n lang="json">
{
  "en": {
    "preview": "Preview",
    "download": "Download",
    "addDocument": "+ Add Document",
    "reallyDeleteDocumentTitle": "Really remove Document?",
    "reallyDeleteDocumentText": "The File will be permanently deleted.",
    "keep": "Keep",
    "noDocuments": "This car has no documents",
    "viewComment": "View Comment",
    "close": "Close",
    "couldNotLoadDocument": "The document could not be loaded. Please try again."
  },
  "de": {
    "preview": "Vorschau",
    "download": "Download",
    "addDocument": "+ Dokument hinzufügen",
    "reallyDeleteDocumentTitle": "Dokument wirklich entfernen?",
    "reallyDeleteDocumentText": "Die Datei wird unwiderruflich gelöscht.",
    "keep": "Behalten",
    "noDocuments": "Dieses Fahrzeug hat noch keine Dokumente",
    "viewComment": "Kommentar anzeigen",
    "close": "Schließen",
    "couldNotLoadDocument": "Das Dokument konnte nicht geladen werden. Bitte versuche es erneut."
  },
  "es": {
    "preview": "Vista previa",
    "download": "Descargar",
    "addDocument": "+ Añadir documento",
    "reallyDeleteDocumentTitle": "¿Eliminar el documento?",
    "reallyDeleteDocumentText": "El archivo se borrará de forma permanente.",
    "keep": "Conservar",
    "noDocuments": "Este vehículo no tiene documentos",
    "viewComment": "Ver comentario",
    "close": "Cerrar",
    "couldNotLoadDocument": "El documento no pudo cargarse. Por favor, inténtalo de nuevo."
  }
}
</i18n>
